import { useMemo } from 'react'
import z from 'zod/v3'
import { getRangeForPreset, type DateRangePreset } from '~/components/ui/date-range-picker'
import { getPreviousPeriodForPreset, type PreviousPeriodValue } from './visioReviews.utils'

/**
 * Période partagée par les cinq onglets, portée par l'URL : un lien pointe sur un onglet **et** sa
 * période. `preset = null` = plage personnalisée, seule à renseigner `from`/`to` (`from` absent =
 * passé illimité).
 */
export const visioReviewsSearchSchema = z.object({
  preset: z
    .enum(['yesterday', 'today', '7d', '30d', '3m', '12m', 'mtd', 'ytd', 'all'])
    .nullable()
    .optional()
    .default('30d'),
  from: z.string().optional(),
  to: z.string().optional(),
})

export type VisioReviewsSearch = z.infer<typeof visioReviewsSearchSchema>

export interface VisioReviewsPeriod {
  dateRange: { from: Date | null; to: Date }
  preset: DateRangePreset | null
  /** null pour « Toujours » (passé illimité) : les onglets masquent alors la colonne de tendance. */
  previousPeriod: PreviousPeriodValue | null
}

export const useVisioReviewsPeriod = (search: VisioReviewsSearch): VisioReviewsPeriod =>
  useMemo(() => {
    const preset = search.preset ?? null
    const dateRange = preset
      ? getRangeForPreset(preset)
      : {
          from: search.from ? new Date(search.from) : null,
          to: search.to ? new Date(search.to) : new Date(),
        }

    return { dateRange, preset, previousPeriod: getPreviousPeriodForPreset(preset, dateRange) }
  }, [search.preset, search.from, search.to])
